/* CycleCountdown — small pill on the Explorer profile that reassures
 * free (and paid) users when their next monthly credit refresh lands.
 * Reads from GET /api/subscription/status; if the tier has no monthly
 * allowance or the cycle_end is missing, the pill renders nothing.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Timer, Infinity as InfinityIcon } from 'lucide-react';
import api from '../lib/api';
import { useT } from '../i18n/I18nContext';

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const end = new Date(iso).getTime();
    const now = Date.now();
    if (isNaN(end)) return null;
    const days = Math.ceil((end - now) / 86_400_000);
    return days;
  } catch { return null; }
}

export default function CycleCountdown() {
  const t = useT();
  const [state, setState] = useState(null);

  useEffect(() => {
    let dead = false;
    api.get('/subscription/status')
      .then(r => { if (!dead) setState(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  if (!state) return null;
  const credits = state.credits || {};
  const allowance = credits.monthly_allowance || 0;

  // Admin / unlimited — no cycle to count down against.
  if (credits.is_unlimited) {
    return (
      <div data-testid="cycle-countdown-unlimited"
        className="rounded-2xl px-4 py-3 flex items-center gap-3 bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-400/40">
        <InfinityIcon className="w-4 h-4 text-amber-300" strokeWidth={2} />
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest text-amber-300">{t('cycleCountdown.unlimited', 'Unlimited')}</p>
          <p className="text-xs text-white/85 mt-0.5">{t('cycleCountdown.unlimitedBody', 'Your account has unlimited Discovery Credits.')}</p>
        </div>
      </div>
    );
  }

  if (allowance <= 0) return null;
  const days = daysUntil(credits.cycle_end);
  if (days === null) return null;

  const remaining = credits.monthly_remaining ?? 0;
  const compounding = false; // Every tier resets — never compounds.
  const daysLabel =
    days <= 0 ? t('cycleCountdown.today', 'today')
    : days === 1 ? t('cycleCountdown.tomorrow', 'tomorrow')
    : t('cycleCountdown.inDays', 'in {n} days', { n: days });

  return (
    <motion.div
      data-testid="cycle-countdown"
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl px-4 py-3 flex items-center gap-3 bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-400/25"
    >
      <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
        <Timer className="w-4 h-4 text-emerald-300" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-emerald-300">{t('cycleCountdown.title', 'Next credit refresh')}</p>
        <p className="text-xs text-white/90 mt-0.5 leading-snug">
          {t('cycleCountdown.body', '{allowance} fresh credits {when}', { allowance, when: daysLabel })}
          <span className="ml-1 text-white/50">· {t('cycleCountdown.remaining', '{n} left', { n: remaining })}</span>
        </p>
      </div>
      <Sparkles className="w-4 h-4 text-emerald-300/70 shrink-0" strokeWidth={1.75} />
    </motion.div>
  );
}
